import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../utils/axios-instance";
import toast from "react-hot-toast";
import ClientDeliveryRoomCard from "../components/vendors/ClientDeliveryRoomCard";

export default function ClientDeliveryRooms({ darkMode }) {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal tracking variables
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [clientName, setClientName] = useState("");
  const [projectName, setProjectName] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Load all accessible delivery rooms from our backend endpoint
  const fetchRooms = async () => {
    try {
      const response = await api.get("/vendors/rooms/");
      setRooms(response.data?.deliveryRooms || []);
    } catch (err) {
      toast.error("Failed to sync client delivery channels");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRooms();
  }, []);

  // Handle building a new room database record
  const handleCreateRoom = async (e) => {
    e.preventDefault();
    if (!clientName.trim() || !projectName.trim()) {
      toast.error("Please supply a valid Client Name and Project Title");
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/vendors/rooms/create/", {
        clientName: clientName.trim(),
        projectName: projectName.trim(),
        description: description.trim(),
      });

      toast.success("Delivery Room generated successfully");
      setIsModalOpen(false);

      // Reset input form attributes
      setClientName("");
      setProjectName("");
      setDescription("");

      // Reload rooms to instantly catch the new array matrix
      fetchRooms();
    } catch (err) {
      toast.error(
        err.response?.data?.error || "Failed to create delivery channel"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Dynamic Theme Definitions
  const theme = {
    bg: darkMode ? "#111827" : "#f9fafb",
    cardBg: darkMode ? "#1f2937" : "#ffffff",
    border: darkMode ? "#374151" : "#e5e7eb",
    textMain: darkMode ? "#f9fafb" : "#111827",
    textSub: darkMode ? "#9ca3af" : "#6b7280",
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
          color: theme.textMain,
        }}
      >
        <p style={{ fontSize: "14px", fontWeight: 500 }}>
          Syncing vendor modules...
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        background: theme.bg,
        minHeight: "100vh",
        padding: "40px",
        fontFamily: "inherit",
      }}
    >
      {/* HEADER MATRIX BANNER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "32px",
        }}
      >
        <div>
          <h1
            style={{
              fontSize: "24px",
              fontWeight: "700",
              color: theme.textMain,
              margin: "0 0 8px 0",
            }}
          >
            Vendor - Client delivery rooms
          </h1>
          <p style={{ fontSize: "14px", color: theme.textSub, margin: 0 }}>
            Package deliverables, collect payment inbound, obtain formal
            approval request pathways, and track audit event signatures.
          </p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          style={{
            background: "#5E5ADB",
            color: "#fff",
            padding: "10px 20px",
            border: "none",
            borderRadius: "6px",
            fontWeight: "500",
            fontSize: "14px",
            cursor: "pointer",
          }}
        >
          + New delivery room
        </button>
      </div>

      {/* DELIVERY WORKSPACE CARDS GRID */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          gap: "24px",
        }}
      >
        {rooms.map((room) => (
          <ClientDeliveryRoomCard
            key={room.id}
            room={room}
            theme={theme}
            onOpen={(r) => navigate(`/vendors/room/${r.id}`)}
            onArchived={(roomId) =>
              setRooms((prev) =>
                prev.map((r) =>
                  r.id === roomId
                    ? { ...r, isArchived: true, status: "Archived" }
                    : r
                )
              )
            }
            onActivated={(roomId) =>
              setRooms((prev) =>
                prev.map((r) =>
                  r.id === roomId
                    ? { ...r, isArchived: false, status: "Active" }
                    : r
                )
              )
            }
          />
        ))}
      </div>

      {/* POPUP SELECTION CREATION FORM OVERLAY MODAL */}
      {isModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: theme.cardBg,
              border: `1px solid ${theme.border}`,
              borderRadius: "12px",
              padding: "28px",
              width: "440px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            }}
          >
            <h2
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: theme.textMain,
                margin: "0 0 4px 0",
              }}
            >
              Create client delivery room
            </h2>
            <p
              style={{
                fontSize: "13px",
                color: theme.textSub,
                marginBottom: "20px",
              }}
            >
              A secure space to review, approve, and release deliverables.
            </p>

            <form onSubmit={handleCreateRoom}>
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: theme.textMain,
                    marginBottom: "6px",
                  }}
                >
                  Client name
                </label>
                <input
                  type="text"
                  required
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: `1px solid ${theme.border}`,
                    borderRadius: "6px",
                    background: theme.bg,
                    color: theme.textMain,
                    fontSize: "14px",
                  }}
                />
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: theme.textMain,
                    marginBottom: "6px",
                  }}
                >
                  Project name
                </label>
                <input
                  type="text"
                  required
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: `1px solid ${theme.border}`,
                    borderRadius: "6px",
                    background: theme.bg,
                    color: theme.textMain,
                    fontSize: "14px",
                  }}
                />
              </div>

              <div style={{ marginBottom: "24px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: theme.textMain,
                    marginBottom: "6px",
                  }}
                >
                  Description (optional)
                </label>
                <textarea
                  rows="3"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: `1px solid ${theme.border}`,
                    borderRadius: "6px",
                    background: theme.bg,
                    color: theme.textMain,
                    fontSize: "14px",
                    resize: "none",
                  }}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "12px",
                }}
              >
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: theme.textSub,
                    padding: "8px 16px",
                    fontSize: "14px",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    background: "#5E5ADB",
                    color: "#fff",
                    border: "none",
                    padding: "8px 20px",
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: "pointer",
                  }}
                >
                  {submitting ? "Creating..." : "Create room"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
